import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Check, X, AlertCircle, Info } from "lucide-react";

type ToastKind = "success" | "info" | "error";

type Toast = {
  id: number;
  message: string;
  kind: ToastKind;
};

type ToastContextValue = {
  push: (message: string, kind?: ToastKind) => void;
};

const Ctx = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastKind, React.ComponentType<{ size?: number; strokeWidth?: number; className?: string; "aria-hidden"?: boolean }>> = {
  success: Check,
  info: Info,
  error: AlertCircle,
};

const STYLES: Record<ToastKind, string> = {
  success: "border-accent/30 bg-accent-soft text-text",
  info: "border-border bg-surface text-text",
  error: "border-danger/30 bg-danger/10 text-text",
};

const ICON_COLORS: Record<ToastKind, string> = {
  success: "text-accent",
  info: "text-text-secondary",
  error: "text-danger",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, kind: ToastKind = "success") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, kind }]);
  }, []);

  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed bottom-6 left-1/2 z-[200] flex w-full max-w-sm -translate-x-1/2 flex-col items-center gap-2 px-4 sm:bottom-8"
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            onDismiss={() =>
              setToasts((prev) => prev.filter((x) => x.id !== t.id))
            }
          />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Mount with transition off, then flip on next frame for slide-in.
    const m = requestAnimationFrame(() => setVisible(true));
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 220);
    }, 3000);
    return () => {
      cancelAnimationFrame(m);
      clearTimeout(t);
    };
  }, [onDismiss]);

  const Icon = ICONS[toast.kind];

  return (
    <div
      className={`pointer-events-auto flex w-full items-start gap-3 rounded-xl border bg-surface px-4 py-3 text-sm shadow-[var(--shadow-card-lift)] transition-all duration-200 ${
        STYLES[toast.kind]
      } ${visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"}`}
      role="status"
    >
      <Icon
        size={16}
        strokeWidth={2.25}
        className={`mt-0.5 shrink-0 ${ICON_COLORS[toast.kind]}`}
        aria-hidden
      />
      <p className="flex-1 leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={() => {
          setVisible(false);
          setTimeout(onDismiss, 220);
        }}
        className="-m-1 rounded-md p-1 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
        aria-label="Fechar"
      >
        <X size={14} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx;
}
